import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { logError } from '../utils/errorLog';
import { clearSavedToolDrafts } from '../utils/toolDrafts';
import Icon from './Icon';
import styles from './ErrorBoundary.module.css';

const Fallback = () => {
  const { t } = useLanguage();
  return (
    <div role="alert" className={styles.crash}>
      <Icon name="error" className={styles.icon} />
      <h2 className={styles.title}>{t('common.crashMessage')}</h2>
      <p className={styles.hint}>{t('common.crashHint')}</p>
      <div className={styles.actions}>
        <button type="button" className={styles.primary} onClick={() => window.location.reload()}>{t('common.reload')}</button>
        {/* A full page load, not a router link: the crash may have left app state broken. */}
        <a className={styles.secondary} href="/">{t('common.backToChat')}</a>
      </div>
    </div>
  );
};

// Catches render errors and failed lazy chunks (e.g. after a deploy) so one
// broken page doesn't blank the whole app.
class ErrorBoundary extends React.Component {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    logError('errorBoundary', error, { componentStack: info?.componentStack?.slice(0, 4000) });
    // Saved tool work (utils/toolDrafts) comes back on every visit, so if a bad AI reply
    // broke the page it would break it again after Reload. Start clean instead. A failed
    // chunk download (after a deploy) is not caused by the work, so keep it then.
    if (error?.name !== 'ChunkLoadError') clearSavedToolDrafts();
  }

  // Clear a crash when resetKey changes. A React `key` would do it too, but it
  // remounts the children, and pages that share a component across routes
  // (/content-generator ↔ /activities) lose their state.
  componentDidUpdate(prevProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  render() {
    return this.state.hasError ? <Fallback /> : this.props.children;
  }
}

export default ErrorBoundary;
